import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Input from 'aquilla/core/molecules/Input';
import Drawer from '@base/Drawer/Drawer';
import { useEffect, useState } from 'react';
import { useSnackbar } from '@stores/snackbar';
import Button from 'aquilla/core/molecules/Button';
import { localStorageAvailable } from '@utils/localStorage';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useCertificationCourseData from './useCertificationCourseData';
import { PROGRAM_LANDING_PAGE } from './constants';

const Container = styled.div`
  display: grid;
  width: 450px;
  grid-column: span 10;
  ${mediaQuery.uptoMobile} {
    width: 328px;
  }
`;

const Title = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    line-height: ${Spacings.SPACING_6B};
  }
`;

const Description = styled(Typography)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_9B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
    margin-bottom: ${Spacings.SPACING_7B};
  }
`;
const FormFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_5B};
  overflow-y: auto;
  max-height: 640px;
  min-height: 450px;
  ${mediaQuery.uptoMobile} {
    height: 380px;
    min-height: 380px;
    gap: ${Spacings.SPACING_4B};
  }
`;

const StyledInput = styled(Input)`
  border: ${({ $hasError }) => {
    return $hasError
      ? '1px solid var(--color-i-red)'
      : '1px solid var( --color-divider)';
  }};
`;

const StyledButton = styled(Button)`
  margin-top: ${Spacings.SPACING_10B};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  width: 176px;
  height: 53px;
  border-radius: ${Spacings.SPACING_1B};
`;

const Form = ({
  goalUID,
  clickedCTA,
  setClickedCTA,
  handleSendEvent,
  handleInterestedGoal
}) => {
  const { formQuestions } = useCertificationCourseData(goalUID);
  const questionDetails = formQuestions?.questionDetails ?? [];
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { open: showSnackbar } = useSnackbar();
  const [drawerState, setDrawerState] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormVErrors] = useState({});

  const handleClose = () => {
    setDrawerState(false);
    setClickedCTA(null);
  };

  const onChange = (e) => {
    const { name } = e.target;
    const { value = '' } = e.target;
    setFormValues({ ...formValues, [name]: value });
    setFormVErrors({ ...formErrors, [name]: value.length === 0 });
  };
  const handleSubmit = async () => {
    const errors = questionDetails.reduce((errorObj, field) => {
      const fieldName = field.name || field.label;
      const fieldValue = formValues[field.name || field.label] || '';
      return { ...errorObj, [fieldName]: fieldValue.trim()?.length === 0 };
    }, {});
    const hasError = Object.values(errors).includes(true);
    if (hasError) {
      setFormVErrors(errors);
    } else if (localStorageAvailable()) {
      let programLandingPageData = localStorage.getItem(PROGRAM_LANDING_PAGE);
      if (programLandingPageData === null) programLandingPageData = [];
      else {
        programLandingPageData = JSON.parse(programLandingPageData);
      }
      const updatedProgramLandingPageData = [
        ...programLandingPageData,
        goalUID
      ];
      localStorage.setItem(
        PROGRAM_LANDING_PAGE,
        JSON.stringify(updatedProgramLandingPageData)
      );
      if (clickedCTA) {
        const submittedFormData = Object.entries(formValues).reduce(
          (submittedValue, fieldValue) => {
            submittedValue.push({
              question: fieldValue[0],
              answer: fieldValue[1]
            });
            return submittedValue;
          },
          []
        );
        handleSendEvent(clickedCTA, {
          form_details: submittedFormData
        });
        showSnackbar({
          type: 'info',
          message:
            'Your details are successfully submitted. Our academic team will get back to you shortly',
          time: 3000
        });
        handleInterestedGoal();
      }

      handleClose(false);
    }
  };
  const hasError = Object.values(formErrors).includes(true);

  useEffect(() => {
    if (isLoggedIn && clickedCTA) {
      let programLandingPageData = null;
      if (localStorageAvailable()) {
        programLandingPageData = localStorage.getItem(PROGRAM_LANDING_PAGE);
        if (programLandingPageData === null) {
          setDrawerState(true);
        } else {
          programLandingPageData = JSON.parse(programLandingPageData);
          setDrawerState(!programLandingPageData.includes(goalUID));
        }
      } else {
        setDrawerState(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, clickedCTA]);

  return (
    <Drawer drawerState={drawerState} onClose={handleClose}>
      <Container>
        <Title variant="h2">{formQuestions?.formTitle}</Title>
        <Description variant="p2" color="var(--color-text-primary)">
          {formQuestions?.formDescription}
        </Description>

        <FormFieldContainer>
          {questionDetails?.map((field) => {
            return (
              <StyledInput
                key={field.uid}
                errorMessage={`${field.label} required`}
                isError={formErrors[field.name || field.label]}
                $hasError={formErrors[field.name || field.label]}
                name={field.name || field.label}
                onChange={onChange}
                placeholder={field.placeholder}
                title={field.label}
                value={formValues[field.name || field.label]}
              />
            );
          })}
        </FormFieldContainer>
        <StyledButton
          onClick={handleSubmit}
          size="medium"
          variant="brand"
          label="Submit"
          disabled={hasError}
        />
      </Container>
    </Drawer>
  );
};

export default Form;
